import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Icon } from "../public/ui";
import { BrandMark } from "../public/PublicHeader";
import { AppNavItem, isNavActive } from "./navItems";
import { daysRemaining, isDateExpired } from "../../utils/helperFuntions";

const itemClass = (active: boolean) =>
  `flex items-center gap-3 px-3 py-2.5 rounded-xl font-label-md text-label-md transition-colors ${
    active
      ? "bg-primary-fixed/60 text-primary font-bold"
      : "text-on-surface-variant hover:bg-surface-container-low hover:text-on-surface"
  }`;

const NavGroup = ({
  item,
  pathname,
  onNavigate,
}: {
  item: AppNavItem;
  pathname: string;
  onNavigate?: () => void;
}) => {
  const hasActiveChild = !!item.children?.some((c) =>
    isNavActive(pathname, c.href),
  );
  const [open, setOpen] = useState(hasActiveChild);

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`w-full ${itemClass(false)} ${hasActiveChild ? "text-on-surface" : ""}`}
      >
        <Icon name={item.icon} className="text-[20px]" />
        <span className="flex-1 text-left">{item.name}</span>
        <Icon
          name="expand_more"
          className={`text-[20px] transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul className="list-none m-0 mt-1 ml-5 pl-3 border-l border-outline-variant/50 flex flex-col gap-1">
          {item.children!.map((child) => (
            <li key={child.name}>
              <Link
                to={child.href!}
                onClick={onNavigate}
                aria-current={
                  isNavActive(pathname, child.href) ? "page" : undefined
                }
                className={itemClass(isNavActive(pathname, child.href))}
              >
                <Icon name={child.icon} className="text-[18px]" />
                {child.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

// Subscription status card at the bottom of the sidebar
const PlanStatus = ({ expDate }: { expDate?: string }) => {
  if (!expDate) return null;

  if (isDateExpired(expDate)) {
    return (
      <div className="rounded-2xl bg-accent-warm/10 p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-accent-warm">
          <Icon name="warning" className="text-[20px]" filled />
          <span className="font-label-md text-label-md font-bold">
            Subscription expired
          </span>
        </div>
        <p className="font-body-sm text-xs text-on-surface-variant">
          You can only practice using one free mock test. Please upgrade your
          plan to practice all the mock tests.
        </p>
        <Link
          to="/package-plan"
          className="h-10 inline-flex items-center justify-center rounded-xl bg-primary text-on-primary font-label-md text-label-md hover:bg-primary-container transition-colors"
        >
          Upgrade Plan
        </Link>
      </div>
    );
  }

  const remaining = Math.max(0, daysRemaining(expDate) ?? 0);
  return (
    <div className="rounded-2xl bg-surface-container-low p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-tertiary" />
        <span className="font-label-md text-label-md text-on-surface font-bold">
          Plan active
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-headline-md text-headline-md text-primary">
          {remaining}
        </span>
        <span className="font-body-sm text-body-sm text-on-surface-variant">
          {remaining === 1 ? "day" : "days"} remaining
        </span>
      </div>
      <span className="font-body-sm text-xs text-on-surface-variant">
        Expires {expDate}
      </span>
    </div>
  );
};

type AppSidebarProps = {
  items: AppNavItem[];
  expDate?: string;
  onNavigate?: () => void;
  onClose?: () => void;
};

export const AppSidebar = ({
  items,
  expDate,
  onNavigate,
  onClose,
}: AppSidebarProps) => {
  const { pathname } = useLocation();

  return (
    <div className="h-full flex flex-col bg-surface-container-lowest font-body-md text-on-surface">
      <div className="h-20 shrink-0 px-6 flex items-center justify-between">
        <BrandMark to="/dashboard" />
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="w-10 h-10 rounded-xl flex items-center justify-center text-on-surface-variant hover:bg-surface-container-low"
          >
            <Icon name="close" className="text-[22px]" />
          </button>
        )}
      </div>
      <nav aria-label="App" className="flex-1 overflow-y-auto px-4 pb-4">
        <ul className="list-none m-0 p-0 flex flex-col gap-1">
          {items.map((item) =>
            item.children ? (
              <NavGroup
                key={item.name}
                item={item}
                pathname={pathname}
                onNavigate={onNavigate}
              />
            ) : (
              <li key={item.name}>
                <Link
                  to={item.href!}
                  onClick={onNavigate}
                  aria-current={
                    isNavActive(pathname, item.href) ? "page" : undefined
                  }
                  className={itemClass(isNavActive(pathname, item.href))}
                >
                  <Icon
                    name={item.icon}
                    className="text-[20px]"
                    filled={isNavActive(pathname, item.href)}
                  />
                  {item.name}
                </Link>
              </li>
            ),
          )}
        </ul>
      </nav>
      <div className="shrink-0 p-4">
        <PlanStatus expDate={expDate} />
      </div>
    </div>
  );
};
